import { useState } from 'react';
import { generateMiniQuiz, submitMiniQuiz, claimQuizPoints, GenerateMiniQuizOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Brain, Loader2, CheckCircle2, XCircle, RotateCcw, Trophy, Star, Zap, Gift, Eye, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { buildQuizResultHtml, openPrintWindow, type PrintAnswer } from '@/lib/quizPrint';

type Question = GenerateMiniQuizOutputType['questions'][0];
type QuizState = 'idle' | 'generating' | 'taking' | 'submitting' | 'results';

export interface PreviousAttempt {
  lastAttemptId: string;
  lastScore: number;
  lastTotal: number;
  lastPercentage: number;
  lastPointsEarned: number;
  bestPercentage: number;
  totalAttempts: number;
}

interface Props {
  sectionId: string;
  sectionTitle: string;
  sectionBody: string;
  previousAttempt?: PreviousAttempt;
  onAttemptComplete?: () => void;
}

const typeLabel: Record<string, string> = {
  single_choice: 'Multiple Choice',
  multiple_choice: 'Select All That Apply',
  yes_no: 'Yes or No',
  true_false: 'True or False',
};

function scoreColor(pct: number) {
  return pct >= 80 ? 'text-green-600' : pct >= 50 ? 'text-yellow-600' : 'text-red-500';
}
function scoreBg(pct: number) {
  return pct >= 80 ? 'bg-green-500/10 border-green-500/20' : pct >= 50 ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-red-500/10 border-red-500/20';
}

export default function MiniQuiz({ sectionId, sectionTitle, sectionBody, previousAttempt, onAttemptComplete }: Props) {
  const [state, setState] = useState<QuizState>('idle');
  const [showPrevious, setShowPrevious] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, number | number[]>>({});
  const [results, setResults] = useState<{ score: number; total: number; percentage: number; pointsEarned: number; correctIds: string[]; newBalance?: number; attemptId?: string } | null>(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimResult, setClaimResult] = useState<{ pointsAwarded: number; message: string } | null>(null);

  const hasPrevious = !!previousAttempt;

  const generate = async () => {
    setShowPrevious(false);
    setState('generating');
    try {
      const res = await generateMiniQuiz({
        sectionId,
        sectionTitle,
        sectionBody,
        variationSeed: attemptCount > 0 || hasPrevious ? `attempt-${(previousAttempt?.totalAttempts ?? 0) + attemptCount}` : undefined,
      });
      setQuestions(res.questions);
      setAnswers({});
      setResults(null);
      setState('taking');
    } catch (e: any) {
      toast.error(e.message || 'Failed to generate quiz');
      setState('idle');
    }
  };

  const handleAnswer = (qId: string, optIdx: number, type: string) => {
    if (type === 'multiple_choice') {
      const current = (answers[qId] as number[]) || [];
      const updated = current.includes(optIdx)
        ? current.filter(i => i !== optIdx)
        : [...current, optIdx];
      setAnswers(prev => ({ ...prev, [qId]: updated }));
    } else {
      setAnswers(prev => ({ ...prev, [qId]: optIdx }));
    }
  };

  const submit = async () => {
    const unanswered = questions.filter(q => answers[q.id] === undefined);
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${unanswered.length} remaining question${unanswered.length > 1 ? 's' : ''}`);
      return;
    }
    setState('submitting');
    try {
      const formatted = questions.map(q => ({ id: q.id, answer: answers[q.id] ?? 0 }));
      const res = await submitMiniQuiz({ sectionId, sectionTitle, questions, answers: formatted });
      setResults({ ...res, total: res.totalQuestions, attemptId: res.attemptId });
      setAttemptCount(c => c + 1);
      setClaimed(false);
      setClaimResult(null);
      setState('results');
      onAttemptComplete?.();
    } catch (e: any) {
      toast.error(e.message || 'Failed to submit quiz');
      setState('taking');
    }
  };

  const retake = () => {
    setState('idle');
    setQuestions([]);
    setAnswers({});
    setResults(null);
    setClaimed(false);
    setClaimResult(null);
  };

  const handleClaim = async () => {
    if (!results?.attemptId) return;
    setClaiming(true);
    try {
      const res = await claimQuizPoints({ attemptId: results.attemptId, sectionId, pointsEarned: results.pointsEarned });
      setClaimed(true);
      setClaimResult({ pointsAwarded: res.pointsAwarded, message: res.message });
      toast.success(res.pointsAwarded > 0 ? `✨ ${res.pointsAwarded} points added to your account!` : res.message);
    } catch (e: any) {
      toast.error(e.message || 'Failed to claim points');
    } finally {
      setClaiming(false);
    }
  };

  const answeredCount = Object.keys(answers).length;

  // ---- IDLE STATE ----
  if (state === 'idle') {
    return (
      <div className="mt-6 border border-primary/20 rounded-xl overflow-hidden">
        {/* Header / CTA */}
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <Brain className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-foreground">Section Quiz</h3>
                {hasPrevious && (
                  <Badge
                    variant="outline"
                    className={cn('text-xs border', scoreBg(previousAttempt!.bestPercentage))}
                  >
                    Best: {previousAttempt!.bestPercentage}% · {previousAttempt!.totalAttempts} attempt{previousAttempt!.totalAttempts !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {hasPrevious
                  ? 'You\'ve already taken this quiz. Retake to improve your score and earn more points!'
                  : 'Test your knowledge on this section. Earn points for correct answers — redeemable for vouchers!'}
              </p>
              <div className="flex flex-wrap gap-2 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-primary" /> 5 pts just for trying</span>
                <span className="flex items-center gap-1"><Star className="w-3 h-3 text-yellow-500" /> 10 pts per correct answer</span>
                <span className="flex items-center gap-1"><Trophy className="w-3 h-3 text-amber-500" /> +20 bonus for 100%</span>
              </div>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <Button onClick={generate} className="gap-2">
                  <Brain className="w-4 h-4" />
                  {hasPrevious ? 'Retake Quiz' : 'Take Quiz'}
                </Button>
                {hasPrevious && (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowPrevious(v => !v)}>
                    <Eye className="w-3.5 h-3.5" />
                    {showPrevious ? 'Hide' : 'View'} Last Result
                    {showPrevious ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Previous result summary */}
        {hasPrevious && showPrevious && (
          <div className="border-t border-border p-4 bg-card">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Last Attempt Result</p>
            <div className={cn('rounded-lg border p-4 flex items-center justify-between gap-4', scoreBg(previousAttempt!.lastPercentage))}>
              <div>
                <p className={cn('text-2xl font-bold', scoreColor(previousAttempt!.lastPercentage))}>
                  {previousAttempt!.lastPercentage}%
                </p>
                <p className="text-sm text-muted-foreground">
                  {previousAttempt!.lastScore}/{previousAttempt!.lastTotal} correct
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">
                  {previousAttempt!.lastPercentage === 100 ? '🏆 Perfect score!' :
                   previousAttempt!.lastPercentage >= 80 ? '🎉 Great job!' :
                   previousAttempt!.lastPercentage >= 50 ? '👍 Good effort' : '📚 Keep studying'}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {previousAttempt!.totalAttempts} total attempt{previousAttempt!.totalAttempts !== 1 ? 's' : ''}
                </p>
                {previousAttempt!.bestPercentage !== previousAttempt!.lastPercentage && (
                  <p className="text-xs text-primary font-medium mt-0.5">
                    Best: {previousAttempt!.bestPercentage}%
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- GENERATING STATE ----
  if (state === 'generating') {
    return (
      <div className="mt-6 border border-border rounded-xl p-8 flex flex-col items-center gap-3 bg-card">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Brain className="w-6 h-6 text-primary animate-pulse" />
        </div>
        <p className="font-medium text-foreground">Generating your quiz…</p>
        <p className="text-sm text-muted-foreground">AI is creating personalised questions from this section</p>
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  // ---- RESULTS STATE ----
  if (state === 'results' && results) {
    const emoji = results.percentage === 100 ? '🏆' : results.percentage >= 80 ? '🎉' : results.percentage >= 50 ? '👍' : '📚';
    return (
      <div className="mt-6 space-y-4">
        {/* Score card */}
        <div className="border border-border rounded-xl bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-lg font-bold text-foreground">{emoji} Quiz Results</p>
              <p className="text-sm text-muted-foreground">{sectionTitle}</p>
            </div>
            <div className="text-right">
              <p className={cn('text-3xl font-bold', scoreColor(results.percentage))}>{results.percentage}%</p>
              <p className="text-sm text-muted-foreground">{results.score}/{results.total} correct</p>
            </div>
          </div>

          {claimed && claimResult ? (
            <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <Gift className="w-5 h-5 text-green-600 shrink-0" />
              <p className="text-sm font-semibold text-foreground">{claimResult.message}</p>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/15">
              <Zap className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">You could earn +{results.pointsEarned} points</p>
                <p className="text-xs text-muted-foreground">
                  5 participation + {results.score * 10} for {results.score} correct{results.score === results.total ? ' + 20 perfect score bonus' : ''}
                </p>
              </div>
              <Button size="sm" onClick={handleClaim} disabled={claiming} className="shrink-0 gap-1.5">
                {claiming ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Claiming…</> : <><Gift className="w-3.5 h-3.5" /> Claim Points</>}
              </Button>
            </div>
          )}
        </div>

        {/* Question breakdown */}
        <div className="space-y-3">
          {questions.map((q) => {
            const isCorrect = results.correctIds.includes(q.id);
            const userAns = answers[q.id];
            const userAnsArr = Array.isArray(userAns) ? userAns : [userAns as number];
            const correctArr = Array.isArray(q.correct) ? q.correct : [q.correct as number];

            return (
              <div key={q.id} className={cn('border rounded-lg p-4', isCorrect ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30')}>
                <div className="flex items-start gap-2 mb-3">
                  {isCorrect
                    ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                  <p className="text-sm font-medium text-foreground">{q.question}</p>
                </div>
                <div className="ml-6 space-y-1 text-xs">
                  {q.options.map((opt, i) => {
                    const wasSelected = userAnsArr.includes(i);
                    const isRight = correctArr.includes(i);
                    return (
                      <div key={i} className={cn('px-2 py-1 rounded flex items-center gap-2',
                        isRight ? 'bg-green-100 text-green-800 font-medium' :
                        wasSelected && !isRight ? 'bg-red-100 text-red-700' : 'text-muted-foreground')}>
                        {isRight ? '✓' : wasSelected ? '✗' : '·'} {opt}
                      </div>
                    );
                  })}
                </div>
                {q.explanation && (
                  <p className="ml-6 mt-2 text-xs text-muted-foreground italic border-t border-border/50 pt-2">
                    💡 {q.explanation}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 gap-2" onClick={retake}>
            <RotateCcw className="w-4 h-4" /> Retake Quiz
          </Button>
          <Button variant="outline" className="flex-1 gap-2" onClick={() => {
            const answersArr: PrintAnswer[] = questions.map(q => ({
              id: q.id,
              answer: answers[q.id] ?? 0,
            }));
            const html = buildQuizResultHtml({
              sectionTitle,
              score: results.score,
              total: results.total,
              percentage: results.percentage,
              questions: questions.map(q => ({
                id: q.id, question: q.question, options: q.options,
                correct: q.correct, explanation: q.explanation ?? '', type: q.type,
              })),
              answersArr,
            });
            openPrintWindow(html);
          }}>
            <Download className="w-4 h-4" /> Download / Print
          </Button>
        </div>
      </div>
    );
  }

  // ---- TAKING STATE ----
  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Brain className="w-4 h-4 text-primary" /> Quiz: {sectionTitle}
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{answeredCount}/{questions.length} answered</span>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={retake}>
            Cancel
          </Button>
        </div>
      </div>

      {questions.map((q, idx) => {
        const userAns = answers[q.id];
        const userAnsArr = Array.isArray(userAns) ? userAns : userAns !== undefined ? [userAns as number] : [];

        return (
          <div key={q.id} className="border border-border rounded-lg bg-card p-4">
            <div className="flex items-start justify-between gap-2 mb-3">
              <p className="text-sm font-medium text-foreground">
                <span className="text-muted-foreground mr-2">Q{idx + 1}.</span>{q.question}
              </p>
              <Badge variant="outline" className="text-xs shrink-0">{typeLabel[q.type] || q.type}</Badge>
            </div>
            <div className="space-y-2">
              {q.options.map((opt, i) => {
                const selected = userAnsArr.includes(i);
                return (
                  <button
                    key={i}
                    onClick={() => handleAnswer(q.id, i, q.type)}
                    className={cn(
                      'w-full text-left text-sm px-3 py-2.5 rounded-lg border transition-all',
                      selected
                        ? 'border-primary bg-primary/10 text-foreground font-medium'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50 text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <span className="font-medium mr-2 text-primary/70">
                      {q.type === 'multiple_choice' ? (selected ? '☑' : '☐') : (selected ? '◉' : '○')}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>
            {q.type === 'multiple_choice' && (
              <p className="text-xs text-muted-foreground mt-2">Select all that apply</p>
            )}
          </div>
        );
      })}

      <Button
        className="w-full"
        onClick={submit}
        disabled={state === 'submitting' || answeredCount < questions.length}
      >
        {state === 'submitting'
          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
          : answeredCount < questions.length
            ? `Answer all questions (${questions.length - answeredCount} remaining)`
            : '🎯 Submit Quiz'}
      </Button>
    </div>
  );
}
